import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

const safeguards = [
  "HIPAA-aligned processing with BAAs on request",
  "AES-256 at rest + TLS 1.2+ in transit",
  "Configurable retention and PHI minimization",
  "Audit-ready access logs and RBAC",
  "No model training on your data"
];

const AiDocumentationSignup = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: ''
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
          },
          emailRedirectTo: `${process.env.REACT_APP_SUPABASE_REDIRECT_URL}/ai-doc/login`,
        }
      });

      if (error) {
        setError(error.message);
        return;
      }

      if (data.user) {
        navigate("/documentation");
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f8f4] via-white to-[#eef4ed] text-[#1f2a3d]">
      <div className="relative mx-auto flex max-w-6xl flex-col gap-10 px-6 pb-16 pt-12 sm:px-10 lg:flex-row lg:px-16">
        <div className="absolute left-8 top-6 h-40 w-40 rounded-full bg-[#8bdf9f]/30 blur-3xl" />
        <div className="absolute right-4 top-24 h-64 w-64 rounded-full bg-[#324705]/10 blur-3xl" />

        <div className="z-10 space-y-6 lg:w-3/5">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/70 px-4 py-2 text-sm font-semibold text-[#324705] shadow-md shadow-[#d8f7e0]">
            New • AI Medical Documentation
          </span>
          <div className="space-y-3">
            <h1 className="text-4xl font-semibold leading-tight sm:text-5xl">
              Stand up HIPAA-ready AI note generation.
            </h1>
            <p className="max-w-2xl text-lg text-[#3b3d24] sm:text-xl">
              Enroll your team to upload visit recordings or secure photos of
              handwritten notes. Get structured, specialty-aware docs with
              compliance baked in.
            </p>
          </div>
          <div className="rounded-3xl border border-[#e7fbec] bg-white/90 p-6 shadow-md shadow-[#eef4ed]">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#3d874e]">
              What you get
            </p>
            <ul className="mt-4 space-y-3 text-sm text-[#1f2a3d]">
              {[
                "Upload audio, dictations, or note photos—web or mobile.",
                "SOAP-style outputs tuned for your specialty and billing codes.",
                "Redline review, versioning, and export to your EHR templates."
              ].map(item => (
                <li key={item} className="flex gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-[#3d874e]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-[#1f2a3d] p-6 text-white shadow-2xl shadow-[#d8f7e0]">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8bdf9f]">
              Security & Compliance
            </p>
            <ul className="mt-4 space-y-3 text-sm">
              {safeguards.map(item => (
                <li key={item} className="flex gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-[#8bdf9f]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="z-10 lg:w-2/5">
          <div className="rounded-3xl bg-white/90 p-8 shadow-2xl shadow-[#d8f7e0] backdrop-blur">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-[#1f2a3d]">Sign up</h2>
              <Link
                to="/ai-doc/login"
                className="text-sm font-semibold text-[#3d874e] hover:underline"
              >
                Already have access?
              </Link>
            </div>
            <p className="mt-2 text-sm text-[#3b3d24]">
              Create an account to start a pilot. We will guide you through BAA
              and permissions.
            </p>

            {error && (
              <div className="mt-4 rounded-2xl bg-red-50 border border-red-200 p-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div>
                <label className="text-sm font-semibold text-[#1f2a3d]">
                  Full name
                </label>
                <input
                  required
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="mt-2 w-full rounded-2xl border border-[#e7fbec] bg-white px-4 py-3 text-sm text-[#1f2a3d] focus:border-[#3d874e] focus:outline-none focus:ring-2 focus:ring-[#d8f7e0]"
                  placeholder="Dr. Ada Lovelace"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-[#1f2a3d]">
                  Work email
                </label>
                <input
                  required
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="mt-2 w-full rounded-2xl border border-[#e7fbec] bg-white px-4 py-3 text-sm text-[#1f2a3d] focus:border-[#3d874e] focus:outline-none focus:ring-2 focus:ring-[#d8f7e0]"
                  placeholder="you@hospital.org"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-[#1f2a3d]">
                  Password
                </label>
                <div className="mt-2 flex items-center gap-2 rounded-2xl border border-[#e7fbec] bg-white px-4 py-1 focus-within:border-[#3d874e] focus-within:ring-2 focus-within:ring-[#d8f7e0]">
                  <input
                    required
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full border-none bg-transparent py-2 text-sm text-[#1f2a3d] outline-none"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-xs font-semibold text-[#3d874e] hover:text-[#324705]"
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-[#324705] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-[#324705]/20 transition hover:-translate-y-0.5 hover:bg-[#3d874e] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Creating account..." : "Create account"}
              </button>
            </form>

            <p className="mt-4 text-xs text-[#58594c]">
              By signing up you agree to our privacy policy and security terms
              for PHI handling.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiDocumentationSignup;
